import { Body } from "p2";

import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { V, V2d } from "../../core/Vector";
import ExplosionEffect from "../effects/ExplosionEffect";
import { Hittable } from "../Hittable";
import { ShieldDamager } from "../interfaces/ShieldDamager";
import { LevelEntity } from "../LevelEntity";

const MAX_DISTANCE_FROM_CENTER = 400;

export enum MunitionType {
  BOMB = "bomb",
  MISSILE = "missile",
  BIG_BOMB = "bigBomb",
}

export interface MunitionMovement {
  update(munition: BaseMunition, dt: number): void;
}

export interface MunitionVisualConfig {
  points: V2d[];
  strokeColor: number;
  strokeWidth?: number;
}

export interface ExplosionConfig {
  radius: number;
  lifetime: number;
  color: number;
  damage: number;
}

export abstract class BaseMunition
  extends LevelEntity
  implements Entity, Hittable, ShieldDamager
{
  body: NonNullable<BaseEntity["body"]>;

  abstract munitionType: MunitionType;
  abstract damage: number;
  exploded: boolean = false;

  abstract getMovementBehavior(): MunitionMovement;
  abstract getVisualConfig(): MunitionVisualConfig;
  abstract getExplosionConfig(): ExplosionConfig;

  protected movement!: MunitionMovement;

  constructor(position: V2d) {
    super();

    // Create physics body - let subclass define the shape
    this.body = this.createBody(position);

    // Set up visual representation - let subclass handle this
    this.setupVisual();
  }

  onAdd() {
    // Initialize after construction when all properties are set
    this.initializeMunition();
  }

  protected initializeMunition() {
    // Get movement behavior from subclass
    this.movement = this.getMovementBehavior();
  }

  protected abstract createBody(position: V2d): Body;
  protected abstract setupVisual(): void;

  onTick(dt: number) {
    if (!this.body || this.exploded || !this.movement) return;

    // Update movement behavior
    this.movement.update(this, dt);

    // Destroy if too far from center
    const distanceToCenter = V(this.body.position).magnitude;
    if (distanceToCenter > MAX_DISTANCE_FROM_CENTER) {
      this.destroy();
    }
  }

  onRender(dt: number) {
    // Update visual position - subclasses can override for custom rendering
    this.updateVisualPosition();
  }

  protected abstract updateVisualPosition(): void;

  explode() {
    if (this.exploded) return;
    this.exploded = true;

    const explosionConfig = this.getExplosionConfig();

    // Create explosion effect
    this.createExplosion(explosionConfig);

    // Allow subclasses to add custom explosion behavior
    this.onExplode();

    this.destroy();
  }

  protected createExplosion(config: ExplosionConfig): void {
    // Dispatch explosion event for sound effects
    this.dispatch("explosion", {
      explosionType: this.getExplosionType(),
      position: this.getPosition(),
      radius: config.radius,
      lifetime: config.lifetime,
    });

    // Create explosion effect with damage
    const explosion = new ExplosionEffect(
      this.getPosition(),
      config.color,
      config.radius,
      config.lifetime,
    );

    // Add explosion to current level
    this.getCurrentLevel()?.addChild(explosion);
  }

  protected abstract getExplosionType(): "bomb" | "bigBomb" | "missile";

  protected onExplode() {
    // Override in subclasses for custom explosion behavior
  }

  takeHit(damage: number) {
    this.explode();
  }

  // ShieldDamager interface implementation
  getShieldDamage(): number {
    return this.damage;
  }

  onShieldHit(): void {
    this.explode();
  }

  onBeginContact(eventData: { other?: Entity }) {
    if (!eventData.other || this.exploded) return;

    // Explode on contact with turret (shield collision is handled by ShieldDamager interface)
    if (eventData.other.tags?.includes("turret")) {
      this.explode();
    }
  }

  getPosition(): V2d {
    return this.body ? V(this.body.position) : V(0, 0);
  }
}
